import React, { useCallback, useMemo } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';

import { formatDateShort } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';
import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { getDateRangeAvailability } from '../../shared/dateRangeAvailability';
import { useHideExpiredRateGroups } from '../../shared/useHideExpiredRateGroups';
import type { ReservationSurchargeAssignment } from '../../types';

import ManageRoomRateSurchargesAction from './ManageRoomRateSurchargesAction';
import { availabilityLabels, getAvailabilityChipColor } from './surchargeAvailability';
import { getUnmatchedCardSx } from './surchargeCardStyles';
import { groupReservationSurcharges } from './surchargeUtils';
import type { RoomRateSurchargeGroup } from './surchargeUtils';
import type { UnmatchedRoomRateCardsProps } from './types';

export default function UnmatchedRoomRateCards({ assignments, hideExpired, now }: UnmatchedRoomRateCardsProps) {
  const roomGroups = useMemo(() => groupReservationSurcharges(assignments), [assignments]);

  const getSurcharges = useCallback((rateGroup: RoomRateSurchargeGroup) => rateGroup.surcharges, []);
  const isExpired = useCallback(
    (assignment: ReservationSurchargeAssignment) =>
      getDateRangeAvailability(assignment.reservationSurcharge, now) === 'expired',
    [now],
  );
  const visibleRateGroups = useHideExpiredRateGroups(roomGroups, getSurcharges, isExpired, hideExpired);

  if (assignments.length === 0) {
    return (
      <Box
        sx={(theme) => ({
          ...getUnmatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">No room-rate surcharge blocks without a matching Salesforce surcharge.</Typography>
      </Box>
    );
  }

  if (visibleRateGroups.every(({ visibleItems: visibleSurcharges }) => visibleSurcharges.length === 0)) {
    return (
      <Box
        sx={(theme) => ({
          ...getUnmatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">All unmatched room-rate surcharge blocks are expired.</Typography>
      </Box>
    );
  }

  return (
    <>
      {visibleRateGroups.map(({ roomGroup, rateGroup, visibleItems: visibleSurcharges }) => {
        const ratePlanLabel = [rateGroup.ratePlanName, rateGroup.ratePlanCode].filter(Boolean).join(' - ');

        if (visibleSurcharges.length === 0) {
          return null;
        }

        return (
          <Box key={rateGroup.roomRateId} sx={getUnmatchedCardSx}>
            <Stack spacing={1.25}>
              <Stack
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
              >
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  <Typography fontWeight={700} variant="body2">
                    {roomGroup.roomName}
                  </Typography>
                  <ManageRoomRateSurchargesAction href={rateGroup.href} />
                </Stack>
                <Typography
                  component={rateGroup.href ? 'a' : 'span'}
                  {...(rateGroup.href ? { href: rateGroup.href, target: '_blank', rel: 'noreferrer' } : {})}
                  color={rateGroup.href ? 'primary' : 'text.secondary'}
                  variant="body2"
                >
                  {ratePlanLabel}
                </Typography>
              </Stack>
              <Stack spacing={0.75}>
                {visibleSurcharges.map((assignment) => {
                  const availability = getDateRangeAvailability(assignment.reservationSurcharge, now);

                  return (
                    <Stack
                      key={assignment.reservationSurcharge.id}
                      alignItems={{ sm: 'center' }}
                      direction={{ sm: 'row' }}
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Typography variant="body2">
                        {formatDateShort(assignment.reservationSurcharge.start_date)} to{' '}
                        {formatDateShort(assignment.reservationSurcharge.end_date)} ·{' '}
                        {daysOfWeekMessage(assignment.reservationSurcharge)}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {currencyFormatter(
                          assignment.reservationSurcharge.cost_currency,
                          assignment.reservationSurcharge.cost_amount,
                        )}{' '}
                        cost ·{' '}
                        {currencyFormatter(
                          assignment.reservationSurcharge.cost_currency,
                          assignment.reservationSurcharge.prices?.[assignment.reservationSurcharge.cost_currency],
                        )}{' '}
                        sell
                      </Typography>
                      <Chip
                        color={getAvailabilityChipColor(availability)}
                        label={availabilityLabels[availability]}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Box>
        );
      })}
    </>
  );
}
